/**
 * HÌNH 5 – Lịch làm việc & Giải quyết vụ án (Thẩm phán)
 * Giao diện kiểu Google Calendar: xem Tuần / Tháng, điều hướng, lưới giờ + ngày.
 */
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  FormControlLabel,
  Checkbox,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
} from '@mui/material'
import { Event, VideoCall, Description, ContentCopy, FlashOn, Add, ChevronLeft, ChevronRight, ViewWeek, CalendarMonth } from '@mui/icons-material'
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageDataState } from '../../components/PageDataState'
import { useGetSessionsQuery } from '../../store/api/courtSessionsApi'
import type { CourtSession } from '../../types/session'

const HOUR_START = 7
const HOUR_END = 19
const SLOTS_PER_HOUR = 2
const ROW_HEIGHT = 48

function getWeekStart(d: Date): Date {
  const x = new Date(d)
  const day = x.getDay()
  const diff = x.getDate() - day + (day === 0 ? -6 : 1)
  x.setDate(diff)
  x.setHours(0, 0, 0, 0)
  return x
}

function getWeekDays(anchor: Date): Date[] {
  const start = getWeekStart(anchor)
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(start)
    x.setDate(start.getDate() + i)
    return x
  })
}

/** Lưới ngày trong tháng (6 tuần, mỗi tuần 7 ngày) để hiển thị tháng kiểu lịch. */
function getMonthGrid(anchor: Date): Date[][] {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  const start = getWeekStart(first)
  const weeks: Date[][] = []
  let cur = new Date(start)
  for (let w = 0; w < 6; w++) {
    const week: Date[] = []
    for (let i = 0; i < 7; i++) {
      week.push(new Date(cur))
      cur.setDate(cur.getDate() + 1)
    }
    weeks.push(week)
  }
  return weeks
}

function getDayKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function getSessionSlotRange(s: CourtSession): { hour: number; minutes: number } {
  const d = new Date(s.scheduled_at)
  return { hour: d.getHours(), minutes: d.getMinutes() }
}

/** Mẫu dự thảo điền sẵn theo loại văn bản (dùng cho Xem & Sửa). */
const DRAFT_TEMPLATES: Record<string, { title: string; content: string }> = {
  summons: {
    title: 'Giấy triệu tập',
    content: `TÒA ÁN NHÂN DÂN QUẬN 1
THÀNH PHỐ HỒ CHÍ MINH

———————————

Số: 01/2026/GT
Ngày: 15/03/2026

GIẤY TRIỆU TẬP
(Tham gia phiên họp kiểm tra chứng cứ)

Kính gửi:
- Nguyên đơn: Lê Hoàng Nam
- Bị đơn: Nguyễn Thị Mai Linh

Vụ án: HN-GD-2026-001 – Tranh chấp hôn nhân và gia đình (ly hôn).

Tòa án nhân dân quận 1 triệu tập các bên tham gia phiên họp kiểm tra chứng cứ vào:

  Thời gian: 08 giờ 00, ngày 25 tháng 03 năm 2026
  Địa điểm: Phòng xử án số 3 – Tòa án nhân dân quận 1 (hoặc tham gia trực tuyến theo link được gửi qua hệ thống).

Các bên mang theo: CMND/CCCD (bản chính), Giấy chứng nhận kết hôn, các tài liệu chứng cứ đã nộp (bản gốc nếu cần đối chiếu).

Nếu vắng mặt không có lý do chính đáng, Tòa án có thể tiến hành xét xử vắng mặt theo quy định pháp luật.

Nơi nhận:
- Nguyên đơn, Bị đơn (đã nêu)
- Lưu hồ sơ vụ án`,
  },
  session_notice: {
    title: 'Thông báo phiên',
    content: `TÒA ÁN NHÂN DÂN QUẬN 1
THÀNH PHỐ HỒ CHÍ MINH

———————————

Số: 02/2026/TB-PH
Ngày: 15/03/2026

THÔNG BÁO PHIÊN HỌP KIỂM TRA CHỨNG CỨ

Kính gửi: Nguyên đơn, Bị đơn (vụ án HN-GD-2026-001).

Tòa án nhân dân quận 1 thông báo phiên họp kiểm tra chứng cứ được tổ chức như sau:

  - Vụ án: HN-GD-2026-001 – Yêu cầu giải quyết ly hôn (Nguyên đơn: Lê Hoàng Nam; Bị đơn: Nguyễn Thị Mai Linh).
  - Thời gian: 08 giờ 00, ngày 25/03/2026.
  - Hình thức: Trực tuyến (link tham gia sẽ được gửi qua Cổng dịch vụ công Tòa án điện tử trước 24 giờ).
  - Thành phần: Thẩm phán Trần Văn Bình; Thư ký phiên tòa; Nguyên đơn, Bị đơn (hoặc người đại diện hợp pháp).

Các bên chuẩn bị chứng cứ, ý kiến và tham gia đúng giờ. Mọi thắc mắc liên hệ bộ phận Thư ký Tòa án qua hệ thống.`,
  },
}

const SESSION_TYPE_LABELS: Record<string, string> = {
  pre_trial: 'Kiểm tra chứng cứ',
  trial: 'Phiên tòa',
  mediation: 'Hòa giải',
}

export default function CalendarPage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const { data: sessions = [], isLoading, error, refetch } = useGetSessionsQuery()
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week')
  const [viewDate, setViewDate] = useState(() => new Date())
  const [openDraft, setOpenDraft] = useState(false)
  const [selectedSession, setSelectedSession] = useState<CourtSession | null>(null)
  const [openCreate, setOpenCreate] = useState(false)
  const defaultCreateDate = (() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    d.setHours(9, 0, 0, 0)
    return d.toISOString().slice(0, 16)
  })()
  const [createCaseCode, setCreateCaseCode] = useState('HN-GD-2026-001')
  const [createType, setCreateType] = useState('pre_trial')
  const [createDate, setCreateDate] = useState(defaultCreateDate)
  const [createToast, setCreateToast] = useState('')
  const [draftPreview, setDraftPreview] = useState<{ title: string; content: string } | null>(null)

  const weekDays = useMemo(() => getWeekDays(viewDate), [viewDate])
  const monthGrid = useMemo(() => getMonthGrid(viewDate), [viewDate])
  const viewStart =
    viewMode === 'week'
      ? weekDays[0]
      : getWeekStart(new Date(viewDate.getFullYear(), viewDate.getMonth(), 1))
  const viewEnd =
    viewMode === 'week'
      ? new Date(weekDays[6].getTime() + 24 * 60 * 60 * 1000 - 1)
      : (() => {
          const start = getWeekStart(new Date(viewDate.getFullYear(), viewDate.getMonth(), 1))
          const end = new Date(start)
          end.setDate(end.getDate() + 6 * 7 - 1)
          end.setHours(23, 59, 59, 999)
          return end
        })()

  const visibleSessions = useMemo(() => {
    const start = viewStart.getTime()
    const end = viewEnd.getTime()
    return sessions.filter((s) => {
      const t = new Date(s.scheduled_at).getTime()
      return t >= start && t <= end
    })
  }, [sessions, viewStart, viewEnd])

  const sessionsByDay = useMemo(() => {
    const map: Record<string, CourtSession[]> = {}
    weekDays.forEach((d) => { map[getDayKey(d)] = [] })
    visibleSessions.forEach((s) => {
      const key = getDayKey(new Date(s.scheduled_at))
      if (map[key]) map[key].push(s)
    })
    return map
  }, [visibleSessions, weekDays])

  const sessionsByDayMonth = useMemo(() => {
    const map: Record<string, CourtSession[]> = {}
    monthGrid.flat().forEach((d) => { map[getDayKey(d)] = [] })
    visibleSessions.forEach((s) => {
      const key = getDayKey(new Date(s.scheduled_at))
      if (map[key]) map[key].push(s)
    })
    return map
  }, [visibleSessions, monthGrid])

  const handleOpenDraft = (s: CourtSession) => {
    setSelectedSession(s)
    setOpenDraft(true)
  }

  const goPrev = () => {
    if (viewMode === 'week') {
      const d = new Date(viewDate)
      d.setDate(d.getDate() - 7)
      setViewDate(d)
    } else {
      const d = new Date(viewDate)
      d.setMonth(d.getMonth() - 1)
      setViewDate(d)
    }
  }
  const goNext = () => {
    if (viewMode === 'week') {
      const d = new Date(viewDate)
      d.setDate(d.getDate() + 7)
      setViewDate(d)
    } else {
      const d = new Date(viewDate)
      d.setMonth(d.getMonth() + 1)
      setViewDate(d)
    }
  }
  const goToday = () => setViewDate(new Date())

  const navLabel =
    viewMode === 'week'
      ? `Tuần ${weekDays[0].getDate()}/${weekDays[0].getMonth() + 1} – ${weekDays[6].getDate()}/${weekDays[6].getMonth() + 1}/${weekDays[6].getFullYear()}`
      : viewDate.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })

  const LEGEND = [
    { label: 'Phiên tòa', color: 'primary.main' },
    { label: 'Hòa giải', color: 'warning.main' },
    { label: 'Kiểm tra chứng cứ', color: 'info.main' },
  ]
  const todayKey = getDayKey(new Date())

return (
    <Box sx={{ width: '100%', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      <Paper variant="outlined" sx={{ width: 240, flexShrink: 0, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {viewDate.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
          </Typography>
          <Box>
            <IconButton size="small" onClick={() => { const d = new Date(viewDate); d.setMonth(d.getMonth() - 1); setViewDate(d) }} aria-label="Tháng trước"><ChevronLeft fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => { const d = new Date(viewDate); d.setMonth(d.getMonth() + 1); setViewDate(d) }} aria-label="Tháng sau"><ChevronRight fontSize="small" /></IconButton>
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, textAlign: 'center', mb: 0.5 }}>
          {['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'].map((w) => (
            <Typography key={w} variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{w}</Typography>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {getMonthGrid(viewDate).flat().map((d) => {
            const key = getDayKey(d)
            const isToday = key === todayKey
            const isCurrentMonth = d.getMonth() === viewDate.getMonth()
            return (
              <Box
                key={key}
                onClick={() => setViewDate(new Date(d))}
                sx={{
                  py: 0.5,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  cursor: 'pointer',
                  bgcolor: isToday ? 'primary.main' : 'transparent',
                  color: isToday ? 'primary.contrastText' : isCurrentMonth ? 'text.primary' : 'text.disabled',
                  fontWeight: isToday ? 700 : 400,
                  '&:hover': { bgcolor: isToday ? 'primary.dark' : 'action.hover' },
                }}
              >
                {d.getDate()}
              </Box>
            )
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, mb: 1 }}>Loại sự kiện</Typography>
        {LEGEND.map((l) => (
          <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: l.color }} />
            <Typography variant="caption">{l.label}</Typography>
          </Box>
        ))}
      </Paper>

      <Box sx={{ flex: 1, minWidth: 320, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="week" aria-label="Xem tuần"><ViewWeek /> Tuần</ToggleButton>
            <ToggleButton value="month" aria-label="Xem tháng"><CalendarMonth /> Tháng</ToggleButton>
          </ToggleButtonGroup>
          <IconButton size="small" onClick={goPrev} aria-label="Trước"><ChevronLeft /></IconButton>
          <Typography variant="body1" fontWeight={600} sx={{ minWidth: 220 }}>{navLabel}</Typography>
          <IconButton size="small" onClick={goNext} aria-label="Sau"><ChevronRight /></IconButton>
          <Button size="small" variant="outlined" onClick={goToday}>Hôm nay</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setOpenCreate(true)}>Tạo phiên</Button>
        </Box>

        <PageDataState isLoading={isLoading} error={error} empty={sessions.length === 0} emptyMessage="Chưa có phiên nào." onRetry={() => refetch()}>
          {viewMode === 'week' ? (
            <Paper variant="outlined" sx={{ flex: 1, minHeight: 520, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Box sx={{ width: 56, flexShrink: 0, borderRight: 1, borderColor: 'divider' }} />
                {weekDays.map((d) => (
                  <Box key={getDayKey(d)} sx={{ flex: 1, minWidth: 0, p: 1, borderRight: 1, borderColor: 'divider', textAlign: 'center', '&:last-child': { borderRight: 0 } }}>
                    <Typography variant="caption" color="text.secondary">{d.toLocaleDateString('vi-VN', { weekday: 'short' })}</Typography>
                    <Typography variant="body2" fontWeight={getDayKey(d) === todayKey ? 700 : 600}>{d.getDate()}/{d.getMonth() + 1}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                <Box sx={{ width: 56, flexShrink: 0, borderRight: 1, borderColor: 'divider' }}>
                  {Array.from({ length: (HOUR_END - HOUR_START) * SLOTS_PER_HOUR }, (_, i) => {
                    const h = HOUR_START + Math.floor(i / SLOTS_PER_HOUR)
                    const isHourStart = i % SLOTS_PER_HOUR === 0
                    return (
                      <Box key={i} sx={{ height: ROW_HEIGHT, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', pr: 0.5, pt: isHourStart ? -0.5 : 0 }}>
                        {isHourStart && <Typography variant="caption" color="text.secondary">{String(h).padStart(2, '0')}:00</Typography>}
                      </Box>
                    )
                  })}
                </Box>
                {weekDays.map((day) => (
                  <Box key={getDayKey(day)} sx={{ flex: 1, minWidth: 0, borderRight: 1, borderColor: 'divider', position: 'relative', '&:last-child': { borderRight: 0 } }}>
                    {Array.from({ length: (HOUR_END - HOUR_START) * SLOTS_PER_HOUR }, (_, i) => (
                      <Box key={i} sx={{ height: ROW_HEIGHT, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }} />
                    ))}
                    {(sessionsByDay[getDayKey(day)] || []).map((s) => {
                      const { hour, minutes } = getSessionSlotRange(s)
                      const topPx = (hour - HOUR_START) * SLOTS_PER_HOUR * ROW_HEIGHT + (minutes / 60) * SLOTS_PER_HOUR * ROW_HEIGHT
                      const durationPx = ROW_HEIGHT * SLOTS_PER_HOUR
                      const isTrial = s.session_type === 'trial'
                      const isMediation = s.session_type === 'mediation'
                      const accent = isTrial ? theme.palette.primary.main : isMediation ? theme.palette.warning.main : theme.palette.info.main
                      const bg = alpha(accent, 0.14)
                      const textColor = isTrial ? theme.palette.primary.dark : isMediation ? theme.palette.warning.dark : theme.palette.info.dark
                      return (
                        <Box
                          key={s.id}
                          onClick={() => handleOpenDraft(s)}
                          sx={{
                            position: 'absolute',
                            left: 4,
                            right: 4,
                            top: topPx + 4,
                            height: durationPx - 8,
                            borderRadius: 1.5,
                            borderLeft: '4px solid',
                            borderLeftColor: accent,
                            bgcolor: bg,
                            color: textColor,
                            p: 1,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            transition: 'box-shadow 0.2s, transform 0.2s',
                            '&:hover': {
                              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                              transform: 'translateY(-1px)',
                              bgcolor: alpha(accent, 0.2),
                            },
                          }}
                        >
                          <Typography variant="caption" fontWeight={700} display="block" noWrap sx={{ fontSize: 12, lineHeight: 1.3 }}>{s.case_code}</Typography>
                          <Typography variant="caption" display="block" sx={{ opacity: 0.9, fontSize: 11, mt: 0.25 }}>{new Date(s.scheduled_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}</Typography>
                        </Box>
                      )
                    })}
                  </Box>
                ))}
              </Box>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 400 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '56px repeat(7, 1fr)', borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ p: 1, borderRight: 1, borderColor: 'divider' }} />
                {['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'].map((w) => (
                  <Box key={w} sx={{ p: 1, textAlign: 'center', borderRight: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>{w}</Typography>
                  </Box>
                ))}
              </Box>
              {monthGrid.map((week, wi) => (
                <Box key={wi} sx={{ display: 'grid', gridTemplateColumns: '56px repeat(7, 1fr)', flex: 1, minHeight: 100 }}>
                  <Box sx={{ p: 0.5, borderRight: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">Tuần {wi + 1}</Typography>
                  </Box>
                  {week.map((d) => {
                    const key = getDayKey(d)
                    const isToday = key === todayKey
                    const isCurrentMonth = d.getMonth() === viewDate.getMonth()
                    const daySessions = sessionsByDayMonth[key] || []
                    return (
                      <Box
                        key={key}
                        sx={{
                          p: 0.5,
                          borderRight: 1,
                          borderBottom: 1,
                          borderColor: 'divider',
                          bgcolor: isToday ? 'action.selected' : 'transparent',
                          minHeight: 96,
                        }}
                      >
                        <Typography variant="body2" fontWeight={isToday ? 700 : 500} color={isCurrentMonth ? 'text.primary' : 'text.disabled'}>{d.getDate()}</Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {daySessions.slice(0, 3).map((s) => {
                            const isTrial = s.session_type === 'trial'
                            const isMediation = s.session_type === 'mediation'
                            const accent = isTrial ? theme.palette.primary.main : isMediation ? theme.palette.warning.main : theme.palette.info.main
                            const bg = alpha(accent, 0.14)
                            const textColor = isTrial ? theme.palette.primary.dark : isMediation ? theme.palette.warning.dark : theme.palette.info.dark
                            return (
                              <Box
                                key={s.id}
                                onClick={() => handleOpenDraft(s)}
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  borderLeft: '3px solid',
                                  borderLeftColor: accent,
                                  bgcolor: bg,
                                  color: textColor,
                                  cursor: 'pointer',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                  transition: 'box-shadow 0.2s, background-color 0.2s',
                                  '&:hover': {
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                    bgcolor: alpha(accent, 0.22),
                                  },
                                }}
                              >
                                <Typography variant="caption" noWrap title={`${s.case_code} – ${new Date(s.scheduled_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}`} sx={{ fontWeight: 600, fontSize: 11 }}>
                                  {new Date(s.scheduled_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} {s.case_code}
                                </Typography>
                              </Box>
                            )
                          })}
                          {daySessions.length > 3 && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: 10 }}>+{daySessions.length - 3} phiên</Typography>
                        )}
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              ))}
            </Paper>
          )}
        </PageDataState>

      <Dialog open={openDraft} onClose={() => setOpenDraft(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlashOn color="primary" /> Phiên: {selectedSession?.case_code ?? selectedSession?.case_id}
        </DialogTitle>
        <DialogContent>
          {selectedSession && (
            <Box>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600}>{selectedSession.case_code ?? selectedSession.case_id}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(selectedSession.scheduled_at).toLocaleString('vi-VN')} · {SESSION_TYPE_LABELS[selectedSession.session_type] ?? selectedSession.session_type}
                </Typography>
                {selectedSession.presiding_judge_name && (
                  <Typography variant="caption" color="text.secondary">Thẩm phán: {selectedSession.presiding_judge_name}</Typography>
                )}
              </Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Tham gia phiên</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Button size="small" variant="contained" startIcon={<VideoCall />} onClick={() => navigate('/workspace/video')}>Vào phiên (Meet)</Button>
                {selectedSession.meet_url && selectedSession.meet_url.startsWith('http') && (
                  <Button size="small" variant="outlined" href={selectedSession.meet_url} target="_blank" rel="noopener noreferrer">Mở link ngoài</Button>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Link họp đã được tạo:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="body2" fontFamily="monospace">meet.uelecourt.vn/{selectedSession.case_code?.toLowerCase().replace(/\//g, '') ?? 'session'}</Typography>
                <IconButton size="small" aria-label="Copy" onClick={() => navigator.clipboard?.writeText(`meet.uelecourt.vn/${selectedSession.case_code ?? ''}`)}><ContentCopy fontSize="small" /></IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Dự thảo đã điền sẵn:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Description fontSize="small" />
                  <Typography variant="body2">Giấy triệu tập</Typography>
                  <Button size="small" variant="outlined" onClick={() => setDraftPreview(DRAFT_TEMPLATES.summons)}>Xem & Sửa</Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Description fontSize="small" />
                  <Typography variant="body2">Thông báo phiên</Typography>
                  <Button size="small" variant="outlined" onClick={() => setDraftPreview(DRAFT_TEMPLATES.session_notice)}>Xem & Sửa</Button>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                3. Gửi thông báo đến các bên:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControlLabel control={<Checkbox defaultChecked />} label="Nguyên đơn" />
                <FormControlLabel control={<Checkbox defaultChecked />} label="Bị đơn" />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDraft(false)}>Hủy</Button>
          <Button variant="contained" color="success" onClick={() => setOpenDraft(false)}>Duyệt & Gửi đi ✓</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!draftPreview} onClose={() => setDraftPreview(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {draftPreview?.title}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, maxHeight: '70vh', overflow: 'auto' }}>
          {draftPreview && (
            <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.7 }}>
              {draftPreview.content}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraftPreview(null)}>Đóng</Button>
          <Button variant="contained" onClick={() => { setDraftPreview(null); setOpenDraft(false); navigate('/workspace/ai-draft'); }}>
            Mở trong Soạn thảo
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Tạo phiên mới</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Vụ án</InputLabel>
            <Select value={createCaseCode} label="Vụ án" onChange={(e) => setCreateCaseCode(e.target.value)}>
              <MenuItem value="HN-GD-2026-001">HN-GD-2026-001</MenuItem>
              <MenuItem value="HN-GD-2026-002">HN-GD-2026-002</MenuItem>
              <MenuItem value="HN-GD-2026-003">HN-GD-2026-003</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Loại phiên</InputLabel>
            <Select value={createType} label="Loại phiên" onChange={(e) => setCreateType(e.target.value)}>
              <MenuItem value="pre_trial">Phiên tòa / Kiểm tra chứng cứ</MenuItem>
              <MenuItem value="mediation">Hòa giải</MenuItem>
              <MenuItem value="hearing">Thẩm vấn</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="Ngày giờ" type="datetime-local" value={createDate} onChange={(e) => setCreateDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mt: 2 }} />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Sau khi tạo, AI sẽ tự tạo link họp trực tuyến và dự thảo Giấy triệu tập, Thông báo phiên.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Hủy</Button>
          <Button variant="contained" onClick={() => { setCreateToast('Đã tạo phiên. Link Meet và dự thảo văn bản sẽ được tạo.'); setOpenCreate(false); setCreateCaseCode('HN-GD-2026-001'); setCreateType('pre_trial'); const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); setCreateDate(d.toISOString().slice(0, 16)); }} disabled={!createCaseCode}>
            Tạo phiên
          </Button>
        </DialogActions>
      </Dialog>

      {createToast && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="success.main">{createToast}</Typography>
        </Box>
      )}
      </Box>
    </Box>
  )
}
